import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo.jsx";
import {
  CATALOG_PRODUCTS,
  CATEGORY_TILES,
  HERO_BG,
  CONTACT,
  WHATSAPP_NUMBER,
  LAUNCH_PLAN_ID,
  findProduct,
  formatPrice,
} from "../data/products.js";
import { useDisabledCategories } from "../hooks/useDisabledCategories.js";
import { useAuth } from "../context/AuthContext.jsx";
import { useJoinLaunchPlan } from "../hooks/useJoinLaunchPlan.js";
import ProductCard from "../components/ProductCard.jsx";
import ProductImage from "../components/ProductImage.jsx";

const STATS = [
  { value: "5,000+", label: "Orders delivered" },
  { value: "4.9★", label: "Customer rating" },
  { value: "Same day", label: "Farm dispatch" },
  { value: "100%", label: "Farm-direct" },
];

const STEPS = [
  { icon: "🛒", n: "01", title: "Pick products", desc: "Browse eggs, milk, chickens & mangoes." },
  { icon: "🛍️", n: "02", title: "Checkout online", desc: "Add your address and review your bag." },
  { icon: "💳", n: "03", title: "Pay securely", desc: "Cards, UPI, wallets & netbanking via Razorpay." },
  { icon: "🚚", n: "04", title: "Enjoy fresh", desc: "Farm-collected, delivered to your door." },
];

const REVIEWS = [
  { name: "Lakshmi R.", city: "Vijayawada", text: "The Banganapalli mangoes were the sweetest we've had in years. Ordering on WhatsApp was so easy.", stars: 5 },
  { name: "Suresh K.", city: "Guntur", text: "Free-range eggs with deep orange yolks, delivered the same morning. We order every week now.", stars: 5 },
  { name: "Anitha M.", city: "Tirupati", text: "Reliable weekly milk supply for our café. Quality is consistent and pricing is fair.", stars: 5 },
];

// Short one-line subtitle per category, matched to the "Shop Farm Fresh" tile
// design — the catalog's real category label carries the tile's headline.
const CATEGORY_SUBTITLE = {
  mangoes: "Seasonal & Local",
  eggs: "Fresh & Healthy",
  milk: "Pure & Natural",
  poultry: "Farm Raised",
  honey: "Wild & Pure",
};

// icon + circle color + short subtitle, in place of the previous longer
// paragraph cards — a compact badge grid reads better at a glance.
const WHY_US = [
  { tone: "orange", title: "Farm-direct", sub: "No middlemen" },
  { tone: "green", title: "Freshly collected", sub: "Sourced regularly" },
  { tone: "blue", title: "Local delivery", sub: "Fresh to your home" },
  { tone: "purple", title: "Easy ordering", sub: "Order online in minutes" },
];

const svg = (paths, extra = "") => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
       strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={extra}>
    {paths}
  </svg>
);

const CartIcon = () => svg(
  <>
    <path d="M4 7h16l-1 4a3 3 0 0 1-3 2H8a3 3 0 0 1-3-2z" />
    <path d="M6 13v7h12v-7" />
    <path d="M9 7V5a3 3 0 0 1 6 0v2" />
  </>
);
const ArrowIcon = () => svg(<path d="M5 12h14M13 5l7 7-7 7" />);
const LeafIcon = () => svg(
  <>
    <path d="M11 20A7 7 0 0 1 4 13V5a1 1 0 0 1 1-1h3a7 7 0 0 1 7 7v9" />
    <path d="M11 20c0-4 2-7 6-9" />
  </>
);
const ClockIcon = () => svg(<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 3" /></>);
const NoPeopleIcon = () => svg(
  <>
    <circle cx="12" cy="8" r="3" />
    <path d="M6 21v-1a6 6 0 0 1 10.9-3.4" />
    <path d="M4 4l16 16" />
  </>
);
const EggIcon = () => svg(<path d="M12 2C8 8 5 12.5 5 16a7 7 0 0 0 14 0c0-3.5-3-8-7-14z" />);
const TruckIcon = () => svg(
  <>
    <path d="M1 3h15v13H1zM16 8h4l3 3v5h-7V8z" />
    <circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />
  </>
);
const ChatIcon = () => svg(
  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
);

const WHY_ICON = { orange: LeafIcon, green: EggIcon, blue: TruckIcon, purple: ChatIcon };

export default function Home() {
  const [pick, setPick] = useState(0);
  const disabledCategories = useDisabledCategories();
  const { membership } = useAuth();
  const joinLaunchPlan = useJoinLaunchPlan();

  // Admin-hidden categories (Admin > Categories) drop out of the shop grid —
  // still reachable via search or a direct link, just not browsed to here.
  const shopProducts = useMemo(
    () => CATALOG_PRODUCTS.filter((p) => !disabledCategories?.has(p.category)),
    [disabledCategories]
  );

  // Rotate through the badged "picks"; fall back to the first few products.
  // Sold-out items (seasonal mangoes) sit out of the rotation — "today's pick"
  // can't be something the customer can't buy today.
  const pickCandidates = useMemo(() => shopProducts.filter((p) => !p.soldOut), [shopProducts]);

  // The ₹9 founding-member plan's own promo banner (mobile) and "Today's
  // picks" rotation (desktop) both drop it once the visitor has already
  // claimed it (any past qualifying order), since re-pitching it would just
  // error at checkout.
  const launchPlan = findProduct(LAUNCH_PLAN_ID);
  const showLaunchPick = Boolean(launchPlan) && !membership?.claimed;

  const PICKS = useMemo(() => {
    const base = pickCandidates.filter((p) => p.badge).length >= 3
      ? pickCandidates.filter((p) => p.badge)
      : pickCandidates.slice(0, 5);
    return showLaunchPick ? [launchPlan, ...base] : base;
  }, [pickCandidates, showLaunchPick, launchPlan]);

  useEffect(() => {
    const t = setInterval(() => setPick((i) => (i + 1) % PICKS.length), 3500);
    return () => clearInterval(t);
  }, [PICKS.length]);

  // The category filter can resolve after this mounts and shrink PICKS —
  // fall back to the first pick rather than crash on a now out-of-range index.
  const featured = PICKS[pick] || PICKS[0];

  const shopCats = CATEGORY_TILES.slice(0, 4);

  return (
    <>
      <Seo
        title="Farm-Fresh Mangoes, Free-Range Eggs, Milk & Poultry"
        description="Buy farm-fresh Banganapalli, Totapuri & Neelam mangoes, free-range eggs, A2 milk, wild honey and country chicken direct from our Andhra Pradesh farm. Order and pay online, delivered to your door."
        path="/"
      />
      {/* HERO — desktop/tablet keeps the original dark-photo hero with the
          rotating "Today's picks" card; phones get the lighter hero2 banner
          below. Both render; CSS shows only one per breakpoint. */}
      <section className="hero">
        <div
          className="hero__bg"
          style={{ backgroundImage: `url(${HERO_BG})` }}
          aria-hidden="true"
        />
        <div className="hero__scrim" aria-hidden="true" />
        <div className="container hero__grid">
          <div className="hero__content">
            <span className="hero__kicker">🌾 Farm-fresh Andhra produce</span>
            <h1>
              Fresh from our farm,
              <br />
              straight to your door
            </h1>
            <p className="hero__sub">
              Seasonal mangoes, free-range eggs, daily milk and live chickens —
              farm-direct with simple online ordering. No middlemen, no markup.
            </p>
            <div className="hero__actions">
              <Link to="/products" className="btn btn--primary btn--lg">Shop now</Link>
              <a href="#how" className="btn btn--light btn--lg">How it works</a>
            </div>
            <div className="hero__points">
              <span>✓ No middlemen</span>
              <span>✓ Daily farm collection</span>
              <span>✓ Retail &amp; bulk orders</span>
            </div>
          </div>

          <aside className="hero__showcase">
            <p className="hero__showcase-label">Today&apos;s picks</p>
            <div className="hero__showcase-stage" key={featured.id}>
              {featured.badge && <span className="pc__badge hero__showcase-badge">{featured.badge}</span>}
              <div className="hero__showcase-media">
                <ProductImage product={featured} className="hero__showcase-img" />
              </div>
              <h3>{featured.name}</h3>
              <p className="hero__showcase-tag">{featured.tag}</p>
              <p className="hero__showcase-price">
                {formatPrice(featured.pricePerKg)}
                {featured.mrp > featured.pricePerKg && <s>{formatPrice(featured.mrp)}</s>}{" "}
                <span>/ {featured.unit}</span>
              </p>
            </div>
            {featured.id === LAUNCH_PLAN_ID ? (
              <button type="button" className="btn btn--primary btn--block" onClick={joinLaunchPlan}>
                Reserve my spot — ₹9
              </button>
            ) : (
              <Link to={`/products?category=${featured.category}`} className="btn btn--primary btn--block">
                Shop {featured.name}
              </Link>
            )}
            <div className="hero__dots" role="tablist" aria-label="Featured products">
              {PICKS.map((p, i) => (
                <button
                  key={p.id}
                  className={`hero__dot ${i === pick ? "is-active" : ""}`}
                  aria-label={`Show ${p.name}`}
                  aria-selected={i === pick}
                  onClick={() => setPick(i)}
                />
              ))}
            </div>
          </aside>
        </div>
      </section>

      {/* Mobile-only hero — see CSS: .hero2 is hidden above the mobile breakpoint. */}
      <section className="hero2">
        <div className="hero2__bg" style={{ backgroundImage: `url(${HERO_BG})` }} aria-hidden="true" />
        <div className="hero2__scrim" aria-hidden="true" />
        <div className="container hero2__inner">
          <h1>
            Farm-fresh food,
            <br />
            delivered to your door.
          </h1>
          <p className="hero2__sub">
            Fresh eggs, milk, seasonal fruits &amp; poultry — sourced directly from local farms.
          </p>
          <div className="hero2__actions">
            <Link to="/products" className="btn btn--primary btn--lg hero2__shop">
              <CartIcon /> Shop Fresh <ArrowIcon />
            </Link>
            <a href="#how" className="btn btn--ghost btn--lg">How It Works</a>
          </div>
          <div className="hero2__feats">
            <span><LeafIcon /> Farm Direct</span>
            <span><ClockIcon /> Fresh Daily</span>
            <span><NoPeopleIcon /> No Middlemen</span>
          </div>
        </div>
      </section>

      {/* SHOP FARM FRESH */}
      <section className="section shopcats">
        <div className="container">
          <div className="shopcats__head">
            <h2>Shop Farm Fresh</h2>
            <Link to="/products" className="shopcats__viewall">View all <span aria-hidden="true">›</span></Link>
          </div>
          <div className="shopcats__grid">
            {shopCats.map((c) => (
              <Link to={`/products?category=${c.id}`} className="shopcat" key={c.id}>
                <span className="shopcat__img"><img src={c.photo} alt="" loading="lazy" /></span>
                <strong>{c.label}</strong>
                <small>{CATEGORY_SUBTITLE[c.id]}</small>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* EGG CLUB PROMO */}
      {showLaunchPick && (
        <section className="section eggpromo-wrap">
          <div className="container">
            <div className="eggpromo">
              <div className="eggpromo__badge">
                <strong>₹9</strong>
                <span>Launch<br />Offer</span>
              </div>
              <div className="eggpromo__body">
                <h3>Egg Club <span aria-hidden="true">🥚</span></h3>
                <p className="eggpromo__sub">Founding members only</p>
                <p className="eggpromo__bullets">Fresh farm eggs • Regular delivery • Better nutrition</p>
                <button type="button" className="btn eggpromo__cta" onClick={joinLaunchPlan}>
                  Join Egg Club <ArrowIcon />
                </button>
              </div>
              <div className="eggpromo__media">
                <ProductImage product={launchPlan} className="eggpromo__img" />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* WHY RIMMONFARM */}
      <section className="section" id="about">
        <div className="container">
          <h2 className="whyus__title">Why RimmonFarm?</h2>
          <div className="whyus__grid">
            {WHY_US.map((w) => {
              const Icon = WHY_ICON[w.tone];
              return (
                <div className="whyus__item" key={w.title}>
                  <span className={`whyus__icon whyus__icon--${w.tone}`}><Icon /></span>
                  <strong>{w.title}</strong>
                  <small>{w.sub}</small>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="section section--soft" id="shop">
        <div className="container">
          <div className="section__head">
            <div>
              <span className="section__tag">Shop now</span>
              <h2>Order fresh from the <strong>farm</strong></h2>
              <p>Tap ADD to build your bag — checkout online in seconds.</p>
            </div>
            <Link to="/products" className="btn btn--ghost">View all products</Link>
          </div>
          <div className="grid">
            {shopProducts.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <div className="trustbar">
        <div className="container trustbar__grid">
          {STATS.map((s) => (
            <div className="trustbar__item" key={s.label}>
              <strong>{s.value}</strong>
              <span>{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* HOW IT WORKS */}
      <section className="section section--soft" id="how">
        <div className="container">
          <div className="section__head section__head--center">
            <div><span className="section__tag">Simple</span><h2>How it <strong>works</strong></h2></div>
          </div>
          <div className="steps">
            {STEPS.map((s) => (
              <div className="step" key={s.n}>
                <div className="step__icon">{s.icon}</div>
                <div className="step__num">STEP {s.n}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section" id="reviews">
        <div className="container">
          <div className="section__head section__head--center">
            <div><span className="section__tag">Reviews</span><h2>Loved by <strong>families &amp; cafés</strong></h2></div>
          </div>
          <div className="reviews">
            {REVIEWS.map((r) => (
              <figure className="review" key={r.name}>
                <div className="review__stars">{"★".repeat(r.stars)}</div>
                <blockquote>{r.text}</blockquote>
                <figcaption>
                  <span className="review__avatar">{r.name.charAt(0)}</span>
                  <span><strong>{r.name}</strong><small>{r.city}</small></span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="section section--soft" id="contact">
        <div className="container">
          <div className="section__head section__head--center">
            <div><span className="section__tag">Contact</span><h2>Get in <strong>touch</strong></h2>
              <p>Message us for today&apos;s stock, delivery routes, and bulk pricing.</p>
            </div>
          </div>
          <div className="contact-cards">
            <a className="contact-card" href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer">
              <span className="contact-card__ico">💬</span>
              <strong>WhatsApp</strong>
              <span>{CONTACT.phoneDisplay}</span>
            </a>
            <a className="contact-card" href={`tel:${CONTACT.phoneTel}`}>
              <span className="contact-card__ico">📞</span>
              <strong>Call us</strong>
              <span>{CONTACT.phoneDisplay}</span>
            </a>
            <a className="contact-card" href={`mailto:${CONTACT.email}`}>
              <span className="contact-card__ico">✉️</span>
              <strong>Email</strong>
              <span>{CONTACT.email}</span>
            </a>
            <div className="contact-card">
              <span className="contact-card__ico">📍</span>
              <strong>Farm location</strong>
              <span>{CONTACT.location}</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="cta-band">
        <div className="cta-band__scrim" aria-hidden="true" />
        <div className="container cta-band__inner">
          <h2>Ready for farm-fresh, delivered?</h2>
          <p>Start your order now or message us on WhatsApp for today&apos;s stock and bulk pricing.</p>
          <div className="hero__actions">
            <Link to="/products" className="btn btn--primary btn--lg">Start your order</Link>
            <Link to="/cart" className="btn btn--light btn--lg">View your bag</Link>
          </div>
        </div>
      </section>
    </>
  );
}
